import type { CupKoBracketOverridesRecord, ScorePair, TeamNamesMap } from '../data/cupScoresData';
import { GENERATIONS, parseCupKoBracketOverride, sanitizeKoOverridesRecord } from '../data/cupScoresData';
import { CUP_ADMIN_PASSWORD } from '../config/cupAdmin';

export const CUP_RESULTS_STORAGE_KEY = 'juniorCupScores_v1';
export const CUP_TEAM_NAMES_STORAGE_KEY = 'juniorCupTeamNames_v1';
export const CUP_KO_OVERRIDES_STORAGE_KEY = 'juniorCupKoOverrides_v1';
export const CUP_SCORES_SYNC_EVENT = 'juniorCupScoresUpdated';

export type CupScoresSyncDetail = { scope: 'results' | 'names' | 'all' };

export type CupLiveData = {
  results: Record<string, ScorePair>;
  teamNames: TeamNamesMap;
  koBracketOverrides?: CupKoBracketOverridesRecord;
  updatedAt: string | null;
};

const API_URL = `${process.env.PUBLIC_URL ?? ''}/api/cup-scores.php`;

export const CUP_LIVE_POLL_MS = 20_000;

function dispatchCupScoresSync(scope: CupScoresSyncDetail['scope']) {
  window.dispatchEvent(new CustomEvent<CupScoresSyncDetail>(CUP_SCORES_SYNC_EVENT, { detail: { scope } }));
}

function parseRecord<T extends Record<string, unknown>>(raw: unknown): T {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {} as T;
  return raw as T;
}

function cacheLocally(data: CupLiveData) {
  try {
    localStorage.setItem(CUP_RESULTS_STORAGE_KEY, JSON.stringify(data.results));
    localStorage.setItem(CUP_TEAM_NAMES_STORAGE_KEY, JSON.stringify(data.teamNames));
    localStorage.setItem(CUP_KO_OVERRIDES_STORAGE_KEY, JSON.stringify(data.koBracketOverrides ?? {}));
  } catch {
    /* ignore */
  }
}

function parseKoOverridesRecord(raw: unknown): CupKoBracketOverridesRecord {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: CupKoBracketOverridesRecord = {};
  for (const y of GENERATIONS) {
    const key = String(y);
    const v = (raw as Record<string, unknown>)[key];
    const parsed = parseCupKoBracketOverride(y, v);
    if (parsed) out[key] = parsed;
  }
  return out;
}

export function loadCupScoresFromCache(): CupLiveData {
  const read = (key: string) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return {};
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  };
  let koRaw: unknown = {};
  try {
    const s = localStorage.getItem(CUP_KO_OVERRIDES_STORAGE_KEY);
    if (s) koRaw = JSON.parse(s) as unknown;
  } catch {
    koRaw = {};
  }
  return {
    results: parseRecord(read(CUP_RESULTS_STORAGE_KEY)),
    teamNames: parseRecord(read(CUP_TEAM_NAMES_STORAGE_KEY)),
    koBracketOverrides: parseKoOverridesRecord(koRaw),
    updatedAt: null,
  };
}

function normalizePayload(raw: unknown): CupLiveData {
  if (!raw || typeof raw !== 'object') {
    return { results: {}, teamNames: {}, koBracketOverrides: {}, updatedAt: null };
  }
  const o = raw as Record<string, unknown>;
  return {
    results: parseRecord(o.results),
    teamNames: parseRecord(o.teamNames),
    koBracketOverrides: parseKoOverridesRecord(o.koBracketOverrides),
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : null,
  };
}

/** Učitaj javne rezultate sa servera preko PHP API-ja (fallback: localStorage). */
export async function fetchCupLiveData(): Promise<CupLiveData> {
  try {
    const res = await fetch(`${API_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (res.ok) {
      const data = normalizePayload(await res.json());
      cacheLocally(data);
      return data;
    }
  } catch {
    /* offline / lokalni dev bez PHP */
  }
  return loadCupScoresFromCache();
}

export type SaveCupLiveResult = { ok: boolean; remote: boolean };

/** Snimi na server (admin); uvek ažurira lokalni keš. */
export async function saveCupLiveData(
  data: Pick<CupLiveData, 'results' | 'teamNames' | 'koBracketOverrides'>,
  password: string = CUP_ADMIN_PASSWORD
): Promise<SaveCupLiveResult> {
  const koBracketOverrides = sanitizeKoOverridesRecord(data.koBracketOverrides ?? {});
  const payload: CupLiveData = {
    results: data.results,
    teamNames: data.teamNames,
    koBracketOverrides,
    updatedAt: new Date().toISOString(),
  };
  cacheLocally(payload);
  dispatchCupScoresSync('all');

  try {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        password,
        results: data.results,
        teamNames: data.teamNames,
        koBracketOverrides: payload.koBracketOverrides,
      }),
    });
    if (res.ok) {
      return { ok: true, remote: true };
    }
  } catch {
    /* PHP nedostupan (npr. lokalni dev) */
  }

  return { ok: true, remote: false };
}
